import { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Eraser, PenLine, Check, Usb, MousePointer2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  isSigWebInstalled,
  startCapture,
  stopCapture,
  clearTablet,
  numberOfPoints,
  getSignatureImage,
} from "@/lib/topaz";
import { whiteToTransparent, getImageSize } from "@/lib/image";

const CANVAS_W = 500;
const CANVAS_H = 170;

export const SignaturePanel = ({ onCaptured }) => {
  const canvasRef = useRef(null);
  const [topazAvailable, setTopazAvailable] = useState(false);
  const [mode, setMode] = useState("mouse"); // 'topaz' | 'mouse'
  const [capturing, setCapturing] = useState(false);
  const [hasInk, setHasInk] = useState(false);

  // mouse drawing refs
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const available = isSigWebInstalled();
    setTopazAvailable(available);
    setMode(available ? "topaz" : "mouse");
  }, []);

  const clearCanvas = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  }, []);

  // ---- Topaz live capture ----
  const handleStartTopaz = () => {
    const c = canvasRef.current;
    clearCanvas();
    const ok = startCapture(c);
    if (ok) {
      setCapturing(true);
      toast.info("Pad Topaz prêt — signez sur l'appareil.");
    } else {
      toast.error("Impossible de démarrer le pad Topaz.");
    }
  };

  // ---- Mouse / touch fallback drawing ----
  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return {
      x: ((point.clientX - rect.left) / rect.width) * CANVAS_W,
      y: ((point.clientY - rect.top) / rect.height) * CANVAS_H,
    };
  };

  const startDraw = (e) => {
    if (mode !== "mouse") return;
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
  };
  const moveDraw = (e) => {
    if (mode !== "mouse" || !drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.strokeStyle = "#0A0A0B";
    ctx.lineWidth = 2.6;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    setHasInk(true);
  };
  const endDraw = () => {
    drawing.current = false;
  };

  // ---- Clear ----
  const handleClear = () => {
    if (mode === "topaz") clearTablet();
    clearCanvas();
  };

  // ---- Validate ----
  const handleValidate = async () => {
    try {
      if (mode === "topaz") {
        if (numberOfPoints() <= 0) {
          toast.error("Aucune signature détectée sur le pad.");
          return;
        }
        const raw = await getSignatureImage(CANVAS_W, CANVAS_H, 4);
        stopCapture();
        setCapturing(false);
        const { dataUrl, width, height } = await whiteToTransparent(raw);
        onCaptured({ dataUrl, width, height });
      } else {
        if (!hasInk) {
          toast.error("Veuillez dessiner une signature.");
          return;
        }
        const dataUrl = canvasRef.current.toDataURL("image/png");
        const { width, height } = await getImageSize(dataUrl);
        onCaptured({ dataUrl, width, height });
      }
      toast.success("Signature capturée — placez-la sur le document.");
    } catch (e) {
      console.error(e);
      toast.error("Échec de la capture de la signature.");
    }
  };

  const switchMode = (m) => {
    if (m === mode) return;
    if (capturing) {
      stopCapture();
      setCapturing(false);
    }
    clearCanvas();
    setMode(m);
  };

  return (
    <div className="space-y-4" data-testid="signature-panel">
      {/* Device status */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span
            className={`h-2 w-2 rounded-full ${topazAvailable ? "bg-[hsl(var(--success))]" : "bg-amber-500"}`}
          />
          <span className="text-sm font-medium" data-testid="topaz-status">
            {topazAvailable ? "Pad Topaz détecté" : "Pad Topaz non détecté"}
          </span>
        </div>
      </div>

      {/* Mode toggle */}
      <div className="grid grid-cols-2 gap-2 rounded-md bg-secondary p-1">
        <button
          data-testid="mode-topaz-btn"
          onClick={() => switchMode("topaz")}
          disabled={!topazAvailable}
          className={`flex items-center justify-center gap-1.5 rounded px-2 py-1.5 text-sm font-medium transition-colors disabled:opacity-40 ${
            mode === "topaz" ? "bg-white shadow-sm text-foreground" : "text-muted-foreground"
          }`}
        >
          <Usb className="h-3.5 w-3.5" /> Pad Topaz
        </button>
        <button
          data-testid="mode-mouse-btn"
          onClick={() => switchMode("mouse")}
          className={`flex items-center justify-center gap-1.5 rounded px-2 py-1.5 text-sm font-medium transition-colors ${
            mode === "mouse" ? "bg-white shadow-sm text-foreground" : "text-muted-foreground"
          }`}
        >
          <MousePointer2 className="h-3.5 w-3.5" /> Souris / tactile
        </button>
      </div>

      {/* Signature canvas */}
      <div className="relative rounded-md border border-input bg-white">
        <canvas
          ref={canvasRef}
          width={CANVAS_W}
          height={CANVAS_H}
          data-testid="sign-canvas"
          onMouseDown={startDraw}
          onMouseMove={moveDraw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={moveDraw}
          onTouchEnd={endDraw}
          className="block w-full touch-none rounded-md"
          style={{ aspectRatio: `${CANVAS_W} / ${CANVAS_H}`, cursor: mode === "mouse" ? "crosshair" : "default" }}
        />
        <div className="pointer-events-none absolute bottom-3 left-4 right-4 flex items-center">
          <div className="h-px flex-1 bg-border" />
          <span className="ml-2 text-[10px] uppercase tracking-widest text-muted-foreground">✕</span>
        </div>
      </div>

      {/* Topaz start button */}
      {mode === "topaz" && (
        <Button
          variant="outline"
          className="w-full"
          onClick={handleStartTopaz}
          data-testid="topaz-start-btn"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          {capturing ? "Recommencer la capture" : "Démarrer la capture"}
        </Button>
      )}

      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={handleClear} data-testid="clear-signature-btn">
          <Eraser className="mr-2 h-4 w-4" /> Effacer
        </Button>
        <Button onClick={handleValidate} data-testid="validate-signature-btn">
          <Check className="mr-2 h-4 w-4" /> Valider
        </Button>
      </div>

      <p className="flex items-start gap-1.5 text-xs leading-relaxed text-muted-foreground">
        <PenLine className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {mode === "topaz"
          ? "Cliquez sur « Démarrer la capture », signez sur le pad Topaz, puis « Valider »."
          : "Dessinez votre signature dans le cadre avec la souris ou le doigt, puis « Valider »."}
      </p>
    </div>
  );
};
